import { createRouter, createWebHistory } from 'vue-router';

import defaultRoutes from '@/router/default-routes';
import recruitmentRoutes from '@/router/recruitment-routes';
import resumeSearchRoutes from '@/router/resume-search';
import appRoutes from '@/router/app-routes';
import logRoutes from '@/router/log-routes';
import settingRoutes from '@/router/setting-routes';
import subscriptionRoutes from '@/router/subscribtion-routes';
import masterViewRoutes from '@/router/master-view-routes';
import clientPortalRoutes from '@/router/client-portal-routes';
import reportRoutes from '@/router/report-routes';
import adminRoutes from '@/router/admin-routes';

import store from '@/store';
import useAuth from '@/composables/auth/useAuth';
import useEnv from '@/composables/app/useEnv';
import axios from 'axios';

const { environment } = useEnv();

const getRoutes = () => {
  let routes = [
    ...defaultRoutes,
    ...recruitmentRoutes,
    ...resumeSearchRoutes,
    ...appRoutes,
    ...logRoutes,
    ...settingRoutes,
    ...subscriptionRoutes,
    ...masterViewRoutes,
    ...clientPortalRoutes,
    ...reportRoutes,
    ...adminRoutes
  ];

  if (environment !== 'production') {
    routes = routes.concat({
      path: '/dev/test',
      name: 'dev-test',
      component: () => import('@/views/DevTest.vue')
    });
  }

  return routes.concat({
    path: '/:pathMatch(.*)*',
    name: '404-not-found',
    component: () => import('@/views/pages/Error404.vue')
  });
};

const router = createRouter({
  // mode: 'history',
  history: createWebHistory(),
  linkExactActiveClass: 'active',
  routes: getRoutes(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  }
});

const {
  verifyAccessToken,
  refreshAccessToken,
  roleMiddleWare,
  getCurrentRole
} = useAuth();

router.beforeEach(async (to, from, next) => {
  let userVerified = await verifyAccessToken();

  if (!userVerified) {
    userVerified = await refreshAccessToken();
  }

  if (to.meta?.layout === 'auth') {
    store.commit('setLayout', 'auth');

    if (userVerified && !to.meta?.allowAuthenticated) {
      return next({ name: 'Home' });
    }
  } else if (to.meta?.layout === 'admin') {
    store.commit('setLayout', 'admin');

    if (!userVerified) {
      return next({ name: 'login' });
    }
  } else {
    store.commit('setLayout', 'app');

    if (!userVerified) {
      return next({ name: 'login' });
    }
  }

  if (to.meta?.superAdmin) {
    const response = await axios.get('/users/super-admin/me/').catch(() => null);
    if (!response?.data?.is_super_admin) {
      store.commit('auth/setSuperAdminAccess', false);
      return next({ name: '404-not-found' });
    }
    store.commit('auth/setSuperAdminAccess', true);
  }

  if (to.meta?.role) {
    const userCurrentRole = await getCurrentRole();

    const userHasPermission = roleMiddleWare(to.meta.role, userCurrentRole);

    if (!userHasPermission) {
      return next({ name: '404-not-found' });
    }

    // Recruiter access to JobTarget is restricted further to super
    // Recruiters. Let the backend permission be authoritative so a direct URL
    // has the same result as the sidebar and cannot expose a broken page.
    if (to.meta?.jobTargetManager && userCurrentRole === 'Recruiter') {
      const response = await axios.get('/jobtarget/status/').catch(() => null);
      const responseStatus = Number(response?.response?.status ?? response?.status);
      if (
        !response ||
        axios.isAxiosError(response) ||
        (Number.isFinite(responseStatus) && responseStatus >= 400)
      ) {
        return next({ name: '404-not-found' });
      }
    }
  }

  next();
});

export default router;
